import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaInfoCircle, FaBell, FaTimes, FaWind, FaBolt, FaCloudRain, FaSnowflake } from 'react-icons/fa';
import { WiThunderstorm, WiFog, WiRain, WiSnow, WiStrongWind, WiDaySunny } from 'react-icons/wi';
import { weatherAPI } from '../api/api';
import { translateAlertText } from '../config/weather';
import '../css/WeatherAlertsWidget.css';

const WeatherAlertsWidget = ({
  coordinates = null,
  onAlertsUpdate = null,
  showNotifications = true
}) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());
  const [lastNotifiedAlerts, setLastNotifiedAlerts] = useState(new Set());

  useEffect(() => {
    if (coordinates) {
      fetchAlerts(coordinates);

      // Poll minden 10 percben
      const intervalId = setInterval(() => {
        fetchAlerts(coordinates);
      }, 600000); // 10 perc

      return () => clearInterval(intervalId);
    }
  }, [coordinates]);

  const fetchAlerts = async (coords) => {
    try {
      setLoading(true);
      setError(null);

      const data = await weatherAPI.getWeatherAlerts(coords.lat, coords.lon);

      if (data && Array.isArray(data.alerts)) {
        setAlerts(data.alerts);

        // Értesítés küldése új sárga vagy magasabb szintű riasztásokról
        if (showNotifications) {
          checkAndNotifyAlerts(data.alerts);
        }

        if (onAlertsUpdate) {
          onAlertsUpdate(data);
        }
      }
    } catch (err) {
      if (err.message !== 'Failed to fetch') {
        console.error('Alerts fetch error:', err);
        setError('Riasztások betöltése sikertelen');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkAndNotifyAlerts = (newAlerts) => {
    newAlerts.forEach(alert => {
      // Csak sárga (moderate) vagy magasabb szintű riasztásoknál
      if (['moderate', 'severe', 'extreme'].includes(alert.severity)) {
        if (!lastNotifiedAlerts.has(alert.id)) {
          sendNotification(alert);
          setLastNotifiedAlerts(prev => new Set([...prev, alert.id]));
        }
      }
    });

    // Töröljük a már nem létező riasztásokat a setből
    const currentAlertIds = new Set(newAlerts.map(a => a.id));
    setLastNotifiedAlerts(prev => {
      const updated = new Set([...prev].filter(id => currentAlertIds.has(id)));
      return updated;
    });
  };

  const sendNotification = (alert) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const severityText = getSeverityText(alert.severity);
      const translatedTitle = translateAlertText(alert.title);
      const translatedDescription = translateAlertText(alert.description);

      new Notification(`⚠️ Időjárási Riasztás - ${severityText}`, {
        body: `${translatedTitle}\n${translatedDescription}`,
        icon: '/weather-alert-icon.png',
        tag: alert.id,
        requireInteraction: alert.severity === 'extreme' || alert.severity === 'severe'
      });
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  const toggleAlert = (alertId) => {
    setExpandedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'extreme': return '#8B0000'; // Dark red
      case 'severe': return '#FF4500';  // Orange-red
      case 'moderate': return '#FFD700'; // Yellow/Gold
      case 'minor': return '#90EE90';    // Light green
      default: return '#888';
    }
  };

  const getAlertTypeIcon = (category, severity) => {
    const categoryLower = category.toLowerCase();

    // Zivatar / Thunderstorm
    if (categoryLower.includes('thunderstorm') || categoryLower.includes('zivatar')) {
      return <WiThunderstorm size={32} />;
    }

    // Szél / Wind
    if (categoryLower.includes('wind') || categoryLower.includes('szél')) {
      return <WiStrongWind size={32} />;
    }

    // Köd / Fog
    if (categoryLower.includes('fog') || categoryLower.includes('köd')) {
      return <WiFog size={32} />;
    }

    // Eső / Rain
    if (categoryLower.includes('rain') || categoryLower.includes('eső')) {
      return <WiRain size={32} />;
    }

    // Hó / Snow
    if (categoryLower.includes('snow') || categoryLower.includes('hó') || categoryLower.includes('ice')) {
      return <WiSnow size={32} />;
    }

    // Hőség / Heat
    if (categoryLower.includes('heat') || categoryLower.includes('hőség') || categoryLower.includes('temperature')) {
      return <WiDaySunny size={32} />;
    }

    // Default fallback based on severity
    switch (severity) {
      case 'extreme':
      case 'severe':
        return <FaExclamationTriangle size={24} />;
      case 'moderate':
        return <FaBell size={24} />;
      case 'minor':
      default:
        return <FaInfoCircle size={24} />;
    }
  };

  const getSeverityText = (severity) => {
    switch (severity) {
      case 'extreme': return 'Extrém';
      case 'severe': return 'Súlyos';
      case 'moderate': return 'Mérsékelt';
      case 'minor': return 'Enyhe';
      default: return severity;
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('hu-HU', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!coordinates) {
    return null;
  }

  if (loading && alerts.length === 0) {
    return (
      <div className="weather-alerts-widget loading">
        <div className="alerts-spinner"></div>
      </div>
    );
  }

  if (error && alerts.length === 0) {
    return null; // Csendes hiba, ne zavarjuk a felhasználót
  }

  if (alerts.length === 0) {
    return null; // Nincs riasztás, ne jelenjen meg a widget
  }

  return (
    <div className="weather-alerts-widget">
      <div className="alerts-header">
        <div className="alerts-title">
          <FaExclamationTriangle className="alerts-title-icon" />
          <h4>Időjárási Riasztások ({alerts.length})</h4>
        </div>
        {showNotifications && Notification.permission === 'default' && (
          <button
            className="enable-notifications-btn"
            onClick={requestNotificationPermission}
            title="Értesítések engedélyezése"
          >
            <FaBell /> Értesítések
          </button>
        )}
      </div>

      <div className="alerts-list">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`alert-item alert-${alert.severity}`}
            style={{ borderLeftColor: getSeverityColor(alert.severity) }}
          >
            <div
              className="alert-header"
              onClick={() => toggleAlert(alert.id)}
            >
              <div className="alert-icon" style={{ color: getSeverityColor(alert.severity) }}>
                {getAlertTypeIcon(alert.category, alert.severity)}
              </div>
              <div className="alert-summary">
                <div className="alert-category">{translateAlertText(alert.category)}</div>
                <div className="alert-severity-badge" style={{ backgroundColor: getSeverityColor(alert.severity) }}>
                  {getSeverityText(alert.severity)}
                </div>
              </div>
              <div className="alert-expand-icon">
                {expandedAlerts.has(alert.id) ? '▼' : '▶'}
              </div>
            </div>

            {expandedAlerts.has(alert.id) && (
              <div className="alert-details">
                <div className="alert-title">{translateAlertText(alert.title)}</div>
                <div className="alert-description">{translateAlertText(alert.description)}</div>

                {alert.affected_areas && alert.affected_areas.length > 0 && (
                  <div className="alert-affected-areas">
                    <strong>Érintett területek:</strong> {alert.affected_areas.map(area => translateAlertText(area)).join(', ')}
                  </div>
                )}

                <div className="alert-times">
                  <div className="alert-time">
                    <strong>Kezdet:</strong> {formatDate(alert.effective_time)}
                  </div>
                  <div className="alert-time">
                    <strong>Lejár:</strong> {formatDate(alert.expiry_time)}
                  </div>
                </div>

                {alert.instructions && (
                  <div className="alert-instructions">
                    <strong>Utasítások:</strong>
                    <p>{translateAlertText(alert.instructions)}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeatherAlertsWidget;
